<script setup lang="ts">
import { ref, watch, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { ChevronLeft, ChevronRight } from "@lucide/vue"
import { cn } from "@/shared/lib/utils"
import type { YearMonth } from "@/shared/lib"

const props = defineProps<{
  modelValue: YearMonth
  min?: YearMonth
  current?: YearMonth
  initialYear: number
}>()

const emit = defineEmits<{
  select: [value: YearMonth]
}>()

const { t, locale } = useI18n()
const displayYear = ref(props.initialYear)

watch(
  () => props.initialYear,
  (y) => { displayYear.value = y },
)

const monthBtnRefs: (HTMLButtonElement | null)[] = Array(12).fill(null)

function focusSelected(): void {
  let btn: HTMLButtonElement | null = null
  if (displayYear.value === props.modelValue.year) {
    const candidate = monthBtnRefs[props.modelValue.month - 1]
    if (candidate && !candidate.disabled) btn = candidate
  }
  if (!btn) {
    btn = monthBtnRefs.find((b): b is HTMLButtonElement => b !== null && !b.disabled) ?? null
  }
  if (btn) nextTick(() => btn!.focus())
}

defineExpose({ focusSelected })

function monthName(month: number): string {
  return new Intl.DateTimeFormat(locale.value, { month: "short" }).format(
    new Date(displayYear.value, month - 1, 1),
  )
}

function isDisabled(month: number): boolean {
  if (!props.min) return false
  if (displayYear.value < props.min.year) return true
  if (displayYear.value === props.min.year && month < props.min.month) return true
  return false
}

function isSelected(month: number): boolean {
  return displayYear.value === props.modelValue.year && month === props.modelValue.month
}

function isCurrent(month: number): boolean {
  return (
    !!props.current &&
    displayYear.value === props.current.year &&
    month === props.current.month
  )
}

function isPrevYearDisabled(): boolean {
  return !!props.min && displayYear.value <= props.min.year
}

function monthAriaLabel(month: number): string {
  const d = new Date(displayYear.value, month - 1, 1)
  let label = new Intl.DateTimeFormat(locale.value, { month: "long", year: "numeric" }).format(d)
  if (isCurrent(month)) label += t("monthPicker.currentSuffix")
  if (isDisabled(month)) label += t("monthPicker.disabledSuffix")
  return label
}

function prevYear(): void {
  if (!isPrevYearDisabled()) displayYear.value--
}

function nextYear(): void {
  displayYear.value++
}

function selectMonth(month: number): void {
  if (isDisabled(month)) return
  emit("select", { year: displayYear.value, month })
}
</script>

<template>
  <div class="flex flex-col gap-1 p-2">
    <div class="flex items-center justify-between border-b border-border px-1 pb-2">
      <button
        type="button"
        :disabled="isPrevYearDisabled()"
        :aria-label="t('monthPicker.prevYear')"
        :class="cn(
          'flex h-11 w-11 items-center justify-center rounded-md md:h-7 md:w-7',
          'text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-40',
        )"
        @click="prevYear"
      >
        <ChevronLeft :size="16" aria-hidden="true" />
      </button>
      <span class="text-sm font-semibold select-none">{{ displayYear }}</span>
      <button
        type="button"
        :aria-label="t('monthPicker.nextYear')"
        :class="cn(
          'flex h-11 w-11 items-center justify-center rounded-md md:h-7 md:w-7',
          'text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )"
        @click="nextYear"
      >
        <ChevronRight :size="16" aria-hidden="true" />
      </button>
    </div>
    <div
      :aria-label="t('monthPicker.title')"
      class="grid grid-cols-3 gap-1 pt-1"
    >
      <button
        v-for="mo in 12"
        :key="mo"
        :ref="(el) => { monthBtnRefs[mo - 1] = el as HTMLButtonElement | null }"
        type="button"
        :disabled="isDisabled(mo)"
        :aria-pressed="isSelected(mo)"
        :aria-label="monthAriaLabel(mo)"
        :class="cn(
          'min-h-[44px] w-full rounded-md text-sm font-medium transition-colors md:h-10 md:min-h-0',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isSelected(mo)
            ? 'bg-primary text-primary-foreground'
            : isCurrent(mo)
              ? 'ring-1 ring-inset ring-ring text-foreground'
              : 'text-foreground',
          !isSelected(mo) && !isDisabled(mo) && 'hover:bg-accent hover:text-accent-foreground',
          isDisabled(mo) && 'cursor-not-allowed opacity-40',
        )"
        @click="selectMonth(mo)"
      >
        {{ monthName(mo) }}
      </button>
    </div>
  </div>
</template>
